import { Link, Navigate } from 'react-router-dom';
import { Building2, CheckCircle, Globe, TrendingUp, FileText, ShoppingCart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AnimatedSection from '../components/AnimatedSection';

const PartnerPortal = () => {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const isPartner = user?.role === 'partner';

  // Redirect based on user role
  if (isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (isPartner) {
    return <Navigate to="/partner/dashboard" replace />;
  }

  return (
    <div className="min-h-screen pt-20 bg-ewss-navy">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-ewss-navy via-ewss-navy to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-ewss-cyan/10 border border-ewss-cyan rounded-full text-ewss-cyan text-sm font-semibold mb-6">
                Wholesale Distribution
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Partner <span className="text-ewss-cyan">Portal</span>
              </h1>
              <p className="text-base md:text-lg text-gray-400 max-w-3xl mx-auto mb-8">
                Join EWSS Automation's wholesale distribution network. Access competitive pricing, technical support, and exclusive partner benefits across Southern Africa.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/partner/apply"
                  className="inline-flex items-center justify-center px-8 py-4 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 transition-all duration-300"
                >
                  <Building2 className="mr-2" size={20} />
                  Apply for Partnership
                </Link>
                
                {!isAuthenticated && (
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center px-8 py-4 border-2 border-ewss-cyan text-ewss-cyan font-semibold rounded-lg hover:bg-ewss-cyan hover:text-ewss-navy transition-all duration-300"
                  >
                    Partner Login
                  </Link>
                )}
              </div>
            </div>
          </AnimatedSection>

          {/* Territory Notice */}
          <AnimatedSection delay={0.2}>
            <div className="max-w-4xl mx-auto mb-16 p-6 bg-black/40 border border-gray-700 rounded-xl">
              <div className="flex items-start gap-4">
                <Globe className="text-ewss-cyan flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-white font-semibold mb-2">Territory Coverage</h3>
                  <p className="text-gray-400 text-sm">
                    EWSS Automation accepts wholesale partners from <strong className="text-white">South Africa and direct neighboring countries</strong> (Namibia, Botswana, Zimbabwe, Mozambique, Eswatini, Lesotho) due to territorial distribution agreements with our brand partners.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <AnimatedSection key={benefit.title} delay={0.3 + index * 0.1}>
                <div className="bg-black/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-ewss-cyan/50 transition-all duration-300">
                  <div className="w-14 h-14 bg-ewss-cyan/10 rounded-lg flex items-center justify-center text-ewss-cyan mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Partner Requirements
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                We're looking for established businesses committed to quality and customer service
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {requirements.map((req, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-ewss-cyan flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="text-white font-semibold mb-1">{req.title}</h4>
                    <p className="text-gray-400 text-sm">{req.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/partner/apply"
              className="inline-block px-8 py-4 bg-ewss-cyan text-ewss-navy font-bold rounded-lg hover:bg-ewss-cyan/90 transition-all duration-300"
            >
              Get Started - Apply Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const benefits = [
  {
    icon: <TrendingUp size={28} />,
    title: 'Competitive Wholesale Pricing',
    description: 'Access tiered pricing with custom discounts based on your volume and partnership level.'
  },
  {
    icon: <FileText size={28} />,
    title: 'Technical Resources',
    description: 'Comprehensive product documentation, datasheets, and installation guides for all brands.'
  },
  {
    icon: <ShoppingCart size={28} />,
    title: 'Flexible Ordering',
    description: 'No minimum order quantities. Order what you need, when you need it with backorder support.'
  },
  {
    icon: <CheckCircle size={28} />,
    title: 'Quality Brands',
    description: 'Authorized distribution of Haiwell, Supmea, Savch and Yotta Control industrial automation products.'
  },
  {
    icon: <Building2 size={28} />,
    title: 'Dedicated Support',
    description: 'Direct access to EWSS technical team for product selection and application support.'
  },
  {
    icon: <Globe size={28} />,
    title: 'Regional Coverage',
    description: 'Serving South Africa and neighboring countries with reliable stock and delivery.'
  }
];

const requirements = [
  {
    title: 'Registered Business',
    description: 'Valid company registration in SA or neighboring countries'
  },
  {
    title: 'Industry Experience',
    description: 'Established business in automation, electrical, or industrial sectors'
  },
  {
    title: 'Technical Capability',
    description: 'Ability to specify and support automation products'
  },
  {
    title: 'Customer Base',
    description: 'Active customer relationships in industrial/commercial sectors'
  }
];

export default PartnerPortal;
